import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { message, Modal } from "antd";
import { foodApiService } from "../services/foodApiService";
import { useFoodManagement } from "../hooks/useFoodManagement";
import { CameraOutlined, FileImageOutlined } from "@ant-design/icons";
import GluceraNavigationRoutes from "../utils/navigationRoutes";
import CameraCapture from "./CameraCapture";
import {
  Home,
  UtensilsCrossed,
  Activity,
  User,
  Camera,
  TrendingUp,
  CheckCircle,
  Brain,
} from "lucide-react";

const BottomNavBar = ({ activeIndex = 0 }) => {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const {
    setScannedData,
    setFoodItemsFromBackend,
    setUploadedImage,
    setDocumentId,
  } = useFoodManagement();

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      setUploadProgress(0);
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev >= 90 ? 90 : prev + Math.random() * 15;
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return newProgress;
        });
      }, 200);
      return foodApiService.predictFood(file);
    },
    onSuccess: response => {
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 1000);

     

      // Store ORIGINAL API response (from _rawResponse field)
      try {
        const rawResponse = (response.data as any)?._rawResponse;
        if (rawResponse) {
          const originalResponseString = JSON.stringify(rawResponse);
          localStorage.setItem("glucera_analyze_response", originalResponseString);
          console.log("✅ BottomNav - Original RAW API response stored:", rawResponse);
          console.log("✅ BottomNav - Stored response has 'date'?", rawResponse.date);
          console.log("✅ BottomNav - Stored response has 'success'?", rawResponse.success);

          // Also store date separately for quick access
          if (rawResponse.date) {
            localStorage.setItem("glucera_meal_date", rawResponse.date);
            console.log("✅ BottomNav Date stored separately:", rawResponse.date);
          }
        } else {
          console.warn("⚠️ BottomNav - No _rawResponse found");
        }
      } catch (error) {
        console.error("❌ BottomNav Error storing original API response:", error);
      }

      const apiData = response.data?.data || response.data;

      // Try different response structures with type assertions
      const responseData: any =
        response.data?.data ?? (response.data as any)?.result ?? response.data;
      const nutrients =
        (responseData as any)?.nutrients ??
        (responseData as any)?.nutrition ??
        (responseData as any)?.nutritional_info;
      const foodItems =
        responseData?.food_items ??
        (responseData as any)?.foods ??
        (responseData as any)?.items ??
        (responseData as any)?.predictions;
      const imageUrl =
        responseData?.image_url ??
        (responseData as any)?.image ??
        (responseData as any)?.url;
      const docId =
        responseData?.document_id ??
        (responseData as any)?.id ??
        (responseData as any)?.doc_id;

      // Check if we have any response data (more lenient like original)
      if (responseData) {
        // Clear localStorage like original
        localStorage.removeItem("glucera_food_items");
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key?.startsWith("glucera_manual_meal_")) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
        });

        // Set data even if some fields are missing
        if (nutrients) {
          setScannedData(nutrients);
        }
        if (foodItems && foodItems.length > 0) {
          setFoodItemsFromBackend(foodItems);
        }
        if (imageUrl) {
          setUploadedImage(imageUrl);
        }
        if (docId) {
          setDocumentId(docId);
        }

        // Navigate regardless
        setTimeout(() => {
          try {
            navigate("/glucera" + GluceraNavigationRoutes.SCANNED_FOOD_DETAIL);
          } catch (navError) {
            window.location.href = "/glucera/scanned-food-detail";
          }
        }, 500);
      } else {
        message.error(
          "No food items detected in the image. Please try again with a clearer image."
        );
      }
    },
    onError: error => {
      setUploadProgress(0);
      message.error("Failed to analyze the image. Please try again.");
    },
  });

  const handleButtonClick = () => {
    setShowModal(true);
  };

  const handleFileUpload = (file: File) => {
    if (file && file.type.startsWith("image/")) {
      // Create blob URL for immediate display
      const blobUrl = URL.createObjectURL(file);
      setUploadedImage(blobUrl);

      // Also convert to base64 for persistence
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        localStorage.setItem("glucera_uploaded_image", base64String);
      };
      reader.readAsDataURL(file);

      uploadMutation.mutate(file);
      setShowModal(false);
    } else {
      message.error("Please select a valid image file");
    }
  };

  const handleCameraCapture = () => {
    setShowCamera(true);
    setShowModal(false);
  };

  const handleCustomCameraCapture = (imageUrl: string, file: File) => {
    setUploadedImage(imageUrl);

    // Convert to base64 for persistence
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      localStorage.setItem("glucera_uploaded_image", base64String);
    };
    reader.readAsDataURL(file);

    uploadMutation.mutate(file);
    setShowCamera(false);
  };

  const handleGalleryUpload = () => {
    galleryInputRef.current?.click();
  };

  return (
    <>
      {/* Custom Camera Capture Overlay */}
      {showCamera && (
        <CameraCapture
          onCapture={handleCustomCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}

      {/* Global Loading Overlay - Same as original UploadCircleButton */}
      {uploadMutation.isPending && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Blur Background */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            style={{ backdropFilter: "blur(8px)" }}
          />

          {/* Modern Scanning Modal - Theme Styled */}
          <div className="relative z-10 mx-4 w-full max-w-[320px] sm:max-w-sm">
            {/* Theme-aware Card Container */}
            <div
              className="overflow-hidden rounded-3xl border-2 border-border bg-card shadow-2xl backdrop-blur-xl"
              style={{
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              }}
            >
              {/* Modern Header - Theme Styled */}
              <div className="px-6 py-6 text-center sm:px-8 sm:py-8">
                {/* Animated Camera Icon */}
                <div className="mb-4 flex justify-center sm:mb-6">
                  <div className="relative">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 shadow-xl sm:h-20 sm:w-20">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-card/20 backdrop-blur-sm sm:h-10 sm:w-10">
                        <div className="border-3 h-5 w-5 animate-spin rounded-full border-white/90 border-t-transparent sm:h-6 sm:w-6" />
                      </div>
                    </div>
                    {/* Pulse Ring */}
                    <div className="absolute inset-0 animate-ping rounded-full border-2 border-primary/40" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="mb-2 flex items-center justify-center gap-2 text-xl font-bold text-gray-900 dark:text-foreground sm:mb-3 sm:text-2xl">
                  <UtensilsCrossed className="h-6 w-6 text-primary" />
                  Analyzing Your Meal
                </h3>

                {/* Status with Icons */}
                <div className="flex items-center justify-center gap-2 px-3 text-sm text-gray-700 dark:text-muted-foreground sm:text-base">
                  {uploadProgress < 30 ? (
                    <>
                      <Camera className="h-4 w-4 text-primary" />
                      <span>Uploading image...</span>
                    </>
                  ) : uploadProgress < 70 ? (
                    <>
                      <Activity className="h-4 w-4 text-primary" />
                      <span>Identifying food items...</span>
                    </>
                  ) : uploadProgress < 90 ? (
                    <>
                      <TrendingUp className="h-4 w-4 text-primary" />
                      <span>Calculating nutrition...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span>Finalizing results...</span>
                    </>
                  )}
                </div>
              </div>

              {/* Progress Section - Theme Styled */}
              <div className="px-6 pb-6 sm:px-8 sm:pb-8">
                {/* Modern Progress Bar */}
                <div className="relative mb-4 sm:mb-5">
                  <div className="h-3 w-full overflow-hidden rounded-full bg-muted shadow-inner sm:h-4">
                    <div
                      className="relative h-full rounded-full bg-gradient-to-r from-primary via-primary/80 to-primary shadow-lg transition-all duration-700 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    >
                      {/* Shine Effect */}
                      <div
                        className="absolute inset-0 animate-pulse opacity-40"
                        style={{
                          background:
                            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)",
                          animation: "slideRight 2s ease-in-out infinite",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Progress Percentage */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-white/80">
                    Progress
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                      {Math.round(uploadProgress)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background">
        <div className="mx-auto max-w-md bg-background px-4 pb-2">
          <div className="relative rounded-2xl border border-border/50 bg-card shadow-lg">
            <div className="grid grid-cols-5 items-center gap-1 px-2 py-2.5">
              {/* Home */}
              <div
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-all hover:bg-accent/50 active:scale-95"
                onClick={() =>
                  navigate("/glucera" + GluceraNavigationRoutes.HOMEPAGE)
                }
              >
                <Home
                  className={`h-6 w-6 transition-colors ${activeIndex === 0 ? "text-primary" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-[10px] font-medium transition-colors ${activeIndex === 0 ? "text-primary" : "text-muted-foreground"}`}
                >
                  Home
                </span>
              </div>

              {/* Meals */}
              <div
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-all hover:bg-accent/50 active:scale-95"
                onClick={() =>
                  navigate("/glucera" + GluceraNavigationRoutes.Mealpage)
                }
              >
                <UtensilsCrossed
                  className={`h-6 w-6 transition-colors ${activeIndex === 1 ? "text-primary" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-[10px] font-medium transition-colors ${activeIndex === 1 ? "text-primary" : "text-muted-foreground"}`}
                >
                  Meals
                </span>
              </div>

              {/* Camera Button - Center */}
              <div className="relative flex items-center justify-center">
                <div className="absolute -top-8">
                  <button
                    className={`flex h-14 w-14 items-center justify-center rounded-full border-4 border-background shadow-2xl transition-all duration-300 ${uploadMutation.isPending
                        ? "animate-pulse cursor-not-allowed"
                        : "hover:scale-110 active:scale-95"
                      }`}
                    onClick={handleButtonClick}
                    disabled={uploadMutation.isPending}
                    style={{
                      backgroundColor: uploadMutation.isPending
                        ? "#1E40AF"
                        : "#3B82F6",
                      boxShadow: "0 20px 40px -12px rgba(59, 130, 246, 0.3)",
                    }}
                  >
                    <Camera className="h-6 w-6 text-white drop-shadow-lg" />
                  </button>

                  {/* File Inputs - Keep gallery input but remove environment (native camera) one since we have custom now */}
                  <input
                    type="file"
                    accept="image/*"
                    ref={galleryInputRef}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileUpload(file);
                      }
                    }}
                    style={{ display: "none" }}
                  />

                  {/* Upload Modal */}
                  <Modal
                    title="Select Image Source"
                    open={showModal}
                    onCancel={() => setShowModal(false)}
                    footer={null}
                    centered
                    className="upload-modal"
                  >
                    <div className="flex justify-center gap-6 py-4">
                      <button
                        className="flex min-w-[120px] flex-col items-center gap-2 rounded-2xl border-2 border-blue-200 p-6 transition-all duration-200 hover:border-blue-400 hover:bg-blue-50"
                        onClick={handleCameraCapture}
                      >
                        <CameraOutlined className="text-3xl text-blue-500" />
                        <span className="text-sm font-medium text-gray-700">
                          Camera
                        </span>
                      </button>
                      <button
                        className="flex min-w-[120px] flex-col items-center gap-2 rounded-2xl border-2 border-green-200 p-6 transition-all duration-200 hover:border-green-400 hover:bg-green-50"
                        onClick={handleGalleryUpload}
                      >
                        <FileImageOutlined className="text-3xl text-green-500" />
                        <span className="text-sm font-medium text-gray-700">
                          Gallery
                        </span>
                      </button>
                    </div>
                  </Modal>
                </div>
              </div>

              {/* Fitness - HIDDEN */}
              {/* 
              <div
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-all hover:bg-accent/50 active:scale-95"
                onClick={() =>
                  navigate("/glucera" + GluceraNavigationRoutes.FITNESS)
                }
              >
                <Activity
                  className={`h-6 w-6 transition-colors ${activeIndex === 2 ? "text-primary" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-[10px] font-medium transition-colors ${activeIndex === 2 ? "text-primary" : "text-muted-foreground"}`}
                >
                  Fitness
                </span>
              </div>
              */}

              {/* Physiological */}
              <div
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-all hover:bg-accent/50 active:scale-95"
                onClick={() =>
                  navigate("/glucera" + GluceraNavigationRoutes.PHYSIOLOGICAL)
                }
              >
                <Brain
                  className={`h-6 w-6 transition-colors ${activeIndex === 3 ? "text-primary" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-[10px] font-medium transition-colors ${activeIndex === 3 ? "text-primary" : "text-muted-foreground"}`}
                >
                  Physio
                </span>
              </div>

              {/* Profile */}
              <div
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-all hover:bg-accent/50 active:scale-95"
                onClick={() => navigate("/glucera/profilepage")}
              >
                <User
                  className={`h-6 w-6 transition-colors ${activeIndex === 4 ? "text-primary" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-[10px] font-medium transition-colors ${activeIndex === 4 ? "text-primary" : "text-muted-foreground"}`}
                >
                  Profile
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BottomNavBar;
